import { SerializedStyles } from '@emotion/react';
import {
  staticCss,
  ADDED_WIDTH,
  TOP_BAR_HEIGHT,
  BUTTON_DEFAULT_CLOSED,
  BUTTON_DEFAULT_OPEN,
  SEARCH_FIELD_DEFAULT_CLOSED,
  SEARCH_FIELD_DEFAULT_OPEN,
  DIALOG_SEARCH_DEFAULT,
  DIALOG_SEARCH_CONTAINER_DEFAULT,
  DIALOG_SEARCH_CONTAINER_SHELL,
  DIALOG_SEARCH_SHELL_NO_MARGIN,
  SIDE_PADDING,
  SHELL_DIALOG_MIN_WIDTH,
} from './styles';
import { ColorDependentStyles, ContainerBounds, LayoutFlags, StyleConfig, Viewport } from './types';

export function computeStyles(
  flags: LayoutFlags,
  bounds: ContainerBounds,
  viewport: Viewport,
  heightOffset: number,
  colorStyles: ColorDependentStyles
): StyleConfig {
  const { hasShellThemeEnabled } = flags;
  const { width: containerWidth } = bounds;
  const dialogWidth = containerWidth + ADDED_WIDTH;

  if (hasShellThemeEnabled) {
    return computeShellStyles(flags, bounds, viewport, dialogWidth, heightOffset, colorStyles);
  }
  return computeDefaultStyles(flags, bounds, viewport, dialogWidth, heightOffset, colorStyles);
}

function computeDefaultStyles(
  flags: LayoutFlags,
  bounds: ContainerBounds,
  viewport: Viewport,
  dialogWidth: number,
  heightOffset: number,
  colorStyles: ColorDependentStyles
): StyleConfig {
  const { isXSmall, isLarge, isExpandedView } = flags;
  const { left: containerLeft, height: containerHeight } = bounds;

  const dialogTop = isExpandedView ? 4 : isLarge ? 4 : containerHeight + 2;

  const dialogLeftPosition = isExpandedView
    ? 0
    : isLarge
    ? containerLeft - SIDE_PADDING
    : (viewport.width - Math.min(viewport.width - 30, 520)) / 2;

  const dialogHeight = isExpandedView ? viewport.height - 10 : ('auto' as const);

  const containerCss: SerializedStyles[] = [staticCss.containerBase];
  if (isLarge) containerCss.push(staticCss.containerLarge);
  if (isXSmall) containerCss.push(staticCss.containerXSmall);

  const topBarRows = !isLarge ? (isExpandedView ? 1 : 2) : 1;

  const dialogCss: SerializedStyles[] = [staticCss.dialogBaseDefault, colorStyles.dialogSizing];
  if (isExpandedView) dialogCss.push(staticCss.dialogExpanded);

  return {
    dialogOffsetMainAxis: -56,
    dialogLeftPosition,
    dialogWidth,
    dialogAnimatedWidth: isExpandedView ? '100%' : dialogWidth,
    dialogTop,
    dialogAnimatedTop: dialogTop,
    dialogHeight,

    containerCss,
    buttonCssClosed: BUTTON_DEFAULT_CLOSED,
    buttonCssOpen: BUTTON_DEFAULT_OPEN,

    searchFieldCssClosed: SEARCH_FIELD_DEFAULT_CLOSED,
    searchFieldCssOpen: SEARCH_FIELD_DEFAULT_OPEN,

    searchFieldContainerCssClosed: [colorStyles.searchFieldContainer],
    searchFieldContainerCssOpen: [colorStyles.searchFieldContainerOpen],

    dialogCss,
    dialogSearchCss: DIALOG_SEARCH_DEFAULT,
    dialogSearchContainerCss: DIALOG_SEARCH_CONTAINER_DEFAULT,

    searchResultsMaxHeight: `calc(100vh - ${topBarRows * TOP_BAR_HEIGHT}px - ${heightOffset}px)`,
  };
}

function computeShellStyles(
  flags: LayoutFlags,
  bounds: ContainerBounds,
  viewport: Viewport,
  dialogWidth: number,
  heightOffset: number,
  colorStyles: ColorDependentStyles
): StyleConfig {
  const { isXSmall, isLarge, isWindows, isExpandedView } = flags;
  const { left: containerLeft, width: containerWidth, height: containerHeight } = bounds;

  const dialogBarOffset = containerHeight + 15;
  const dialogTop = isLarge ? 0 : dialogBarOffset;

  const dialogLeftPosition = (() => {
    if (isExpandedView) return 0;
    if (containerWidth >= SHELL_DIALOG_MIN_WIDTH) return containerLeft - SIDE_PADDING;
    if (!isXSmall) {
      return Math.max(SIDE_PADDING, containerLeft - SIDE_PADDING - (SHELL_DIALOG_MIN_WIDTH - containerWidth));
    }
    return (viewport.width - Math.min(viewport.width - 30, 520)) / 2;
  })();

  const dialogHeight = isExpandedView ? viewport.height - 60 : ('auto' as const);

  const containerCss: SerializedStyles[] = [staticCss.containerBase];
  if (isLarge) {
    containerCss.push(staticCss.containerLarge);
  } else {
    containerCss.push(staticCss.containerAlignEnd);
  }
  if (isXSmall) containerCss.push(staticCss.containerXSmall);
  containerCss.push(isXSmall && isWindows ? staticCss.containerMarginLeftAuto : staticCss.containerMarginRightAuto);

  const dialogCss: SerializedStyles[] = [staticCss.dialogBaseShell, colorStyles.dialogSizing];
  if (isExpandedView) dialogCss.push(staticCss.dialogExpandedShell);

  const dialogSearchMarginTop = isExpandedView ? 10 : 0;

  return {
    dialogOffsetMainAxis: isExpandedView ? -40 : isXSmall ? -10 : -40,
    dialogLeftPosition,
    dialogWidth,
    dialogAnimatedWidth: isExpandedView ? '100%' : dialogWidth,
    dialogTop,
    dialogAnimatedTop: dialogTop,
    dialogHeight,

    containerCss,
    buttonCssClosed: colorStyles.shellBtnClosed,
    buttonCssOpen: colorStyles.shellBtnOpen,

    searchFieldCssClosed: [colorStyles.shellSearchField],
    searchFieldCssOpen: [colorStyles.shellSearchField, staticCss.opacityHidden],

    searchFieldContainerCssClosed: [colorStyles.shellSearchFieldContainer],
    searchFieldContainerCssOpen: [colorStyles.shellSearchFieldContainerOpen],

    dialogCss,
    dialogSearchCss:
      dialogSearchMarginTop === 0 ? DIALOG_SEARCH_SHELL_NO_MARGIN : [colorStyles.dialogSearchShellWithMargin],
    dialogSearchContainerCss: DIALOG_SEARCH_CONTAINER_SHELL,

    searchResultsMaxHeight: `calc(100vh - ${1.5 * TOP_BAR_HEIGHT}px - ${heightOffset}px)`,
  };
}
